import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CiPageChrome from '../../components/CustomerIdentity/CiPageChrome';
import {
  CIConfirmPopup,
  CIDrawer,
  CIInfoBar,
} from '../../components/CustomerIdentity/CustomerIdentityShared';
import { useCITranslate } from '../../i18n/customerIdentity';
import {
  useCiAdminAuth,
  useCiAdminBasePath,
} from '../../contexts/CiAdminAuthContext';
import EntitySettingsPanel from '../../components/CustomerIdentity/EntitySettingsPanel';
import SiteForm from '../../components/CustomerIdentity/SiteForm';
import {
  createSite,
  deleteSite,
  listSites,
  updateSite,
} from '../../services/customerIdentityApi';

export default function Sites() {
  const t = useCITranslate();
  const navigate = useNavigate();
  const { principal } = useCiAdminAuth();
  const basePath = useCiAdminBasePath();
  const canManageSites = principal?.scope === 'all';
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const settingsRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listSites(true);
      setSites(data || []);
    } catch (err) {
      setMessage({ kind: 'danger', text: err.message || 'Could not load sites.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (values) => {
    setBusy(true);
    try {
      let saved;
      if (editing) saved = await updateSite(editing.id, values);
      else saved = await createSite(values);
      try {
        await settingsRef.current?.flush(saved?.id);
      } catch (settingsErr) {
        setMessage({
          kind: 'danger',
          text: settingsErr.message || 'Site saved but settings failed.',
        });
        return;
      }
      setEditing(null);
      setCreating(false);
      await load();
      setMessage({ kind: 'success', text: 'Site saved.' });
    } catch (err) {
      setMessage({ kind: 'danger', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteSite(pendingDelete.id);
      setPendingDelete(null);
      await load();
      setMessage({ kind: 'success', text: 'Site deleted.' });
    } catch (err) {
      setMessage({ kind: 'danger', text: err.message || 'Delete failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <CIInfoBar message={message} onDismiss={() => setMessage(null)} />
      <CiPageChrome
        title={t('sites.pageTitle')}
        subtitle={
          canManageSites
            ? t('sites.chromeSubtitle')
            : `Showing ${sites.length} assigned site${sites.length === 1 ? '' : 's'}.`
        }
        actions={canManageSites ? (
          <button
            type="button"
            className="btn ci-btn-primary text-white"
            onClick={() => setCreating(true)}
          >
            <i className="bi bi-plus-lg me-1" />
            {t('sites.newSite')}
          </button>
        ) : null}
      >
        <div className="container-fluid px-0 px-md-1">
          <div className="ci-card ci-card-flush">
            <div className="p-0">
              {loading ? (
                <div className="p-3 text-muted">Loading…</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>URL</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No sites yet. Create your first site to get started.
                          </td>
                        </tr>
                      ) : null}
                      {sites.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <div className="fw-semibold">{s.name}</div>
                            {s.description ? (
                              <div className="text-muted small">{s.description}</div>
                            ) : null}
                            <div className="text-muted small">id: {s.id}</div>
                          </td>
                          <td>
                            {s.url ? (
                              <a href={s.url} target="_blank" rel="noreferrer">
                                {s.url}
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${s.is_active ? 'bg-success' : 'bg-secondary'}`}>
                              {s.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => navigate(`${basePath}/sites/${s.id}`)}
                              >
                                Open
                              </button>
                              {canManageSites ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setEditing(s)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => setPendingDelete(s)}
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </CiPageChrome>

      <CIDrawer
        open={creating || Boolean(editing)}
        title={editing ? 'Edit site' : 'New site'}
        onClose={() => { setCreating(false); setEditing(null); }}
      >
        <SiteForm
          site={editing}
          onSubmit={handleSubmit}
          onCancel={() => { setCreating(false); setEditing(null); }}
          busy={busy}
        >
          <EntitySettingsPanel
            ref={settingsRef}
            scope="site"
            scopeId={editing?.id || null}
            contextSiteId={editing?.id || null}
            defaultExpanded={Boolean(editing)}
            onError={(err) =>
              setMessage({ kind: 'danger', text: err.message || 'Could not load settings.' })
            }
          />
        </SiteForm>
      </CIDrawer>

      <CIConfirmPopup
        open={Boolean(pendingDelete)}
        title="Delete site"
        message={`Delete site "${pendingDelete?.name}"? This will also delete all associated customers, users, and data. This cannot be undone.`}
        confirmLabel="Delete site"
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
